(function () {
  'use strict';

  const REF_KEY = 'balour_partner_ref_v1';
  const MAX_AGE = 90 * 24 * 60 * 60 * 1000;
  const DEFAULT_AGE = 30 * 24 * 60 * 60 * 1000;
  let syncing = false;
  let clearAfterSync = false;
  let pendingIncoming = null;
  let pendingPartnerCode = null;
  let pendingLandingPath = '/';
  let pendingUtm = {};

  function attributionAllowed() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return Boolean(privacy && typeof privacy.marketingAllowed === 'function' && privacy.marketingAllowed());
    } catch (error) { return false; }
  }

  function tokenExpiry(token) {
    const now = Date.now();
    try {
      const segment = token.split('.')[0];
      if (!segment) return now + DEFAULT_AGE;
      const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')));
      const expires = Number(payload.exp) * 1000;
      if (Number.isFinite(expires)) return expires > now ? Math.min(expires, now + MAX_AGE) : 0;
    } catch (error) { /* The server remains responsible for signature and expiry validation. */ }
    return now + DEFAULT_AGE;
  }

  function validToken(token) {
    return typeof token === 'string' && token.length >= 16 && token.length <= 2048 && /^[A-Za-z0-9._~-]+$/.test(token);
  }

  function read(storage) {
    try {
      const value = JSON.parse(storage.getItem(REF_KEY) || 'null');
      if (!value || !validToken(value.token) || Number(value.expiresAt) <= Date.now()) {
        storage.removeItem(REF_KEY);
        return null;
      }
      return value;
    } catch (error) { return null; }
  }

  function save(token) {
    if (!attributionAllowed()) return null;
    const value = { token, expiresAt: tokenExpiry(token) };
    if (value.expiresAt <= Date.now()) return null;
    try { sessionStorage.setItem(REF_KEY, JSON.stringify(value)); } catch (error) { /* Session storage may be unavailable. */ }
    try { localStorage.setItem(REF_KEY, JSON.stringify(value)); } catch (error) { /* Persistent storage may be unavailable. */ }
    return value;
  }

  function clearStored() {
    try { sessionStorage.removeItem(REF_KEY); } catch (error) { /* Ignore. */ }
    try { localStorage.removeItem(REF_KEY); } catch (error) { /* Ignore. */ }
  }

  function hasStoredReferral() {
    try {
      return Boolean(sessionStorage.getItem(REF_KEY) || localStorage.getItem(REF_KEY));
    } catch (error) { return false; }
  }

  function current() {
    if (!attributionAllowed()) return null;
    const sessionValue = read(sessionStorage);
    if (sessionValue) return sessionValue;
    const localValue = read(localStorage);
    if (localValue) {
      try { sessionStorage.setItem(REF_KEY, JSON.stringify(localValue)); } catch (error) { /* Ignore. */ }
    }
    return localValue;
  }

  function removeReferralFromUrl(url) {
    url.searchParams.delete('ba_ref');
    url.searchParams.delete('ba_partner');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }

  async function exchangePartnerCode() {
    if (!pendingPartnerCode || !attributionAllowed()) return null;
    const code = pendingPartnerCode;
    const params = new URLSearchParams({ to: pendingLandingPath });
    Object.entries(pendingUtm).forEach(([key, value]) => params.set(key, value));
    try {
      const response = await fetch(`/apps/balour-partners/r/${encodeURIComponent(code)}?${params}`, { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
      if (!response.ok) throw new Error('Partner token exchange failed');
      const body = await response.json();
      if (!validToken(body.token)) throw new Error('Partner token invalid');
      pendingPartnerCode = null;
      pendingUtm = {};
      const saved = save(body.token);
      if (saved) await syncCart(true);
      return saved;
    } catch (error) { return null; }
  }

  async function syncCart(force) {
    if (syncing || !attributionAllowed()) return;
    const value = current();
    if (!value) return;
    syncing = true;
    try {
      let shouldUpdate = Boolean(force);
      if (!shouldUpdate) {
        const cartResponse = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
        if (!cartResponse.ok) throw new Error('Cart unavailable');
        const cart = await cartResponse.json();
        shouldUpdate = !cart.attributes || cart.attributes._balour_partner_ref !== value.token;
      }
      if (shouldUpdate) {
        if (!attributionAllowed()) return;
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _balour_partner_ref: value.token } })
        });
      }
    } catch (error) { /* Attribution can retry on the next cart event or page view. */ }
    finally {
      syncing = false;
      if (clearAfterSync) {
        clearAfterSync = false;
        clearCartAttribution();
      }
    }
  }

  async function clearCartAttribution() {
    if (syncing) {
      clearAfterSync = true;
      return;
    }
    syncing = true;
    try {
      const cartResponse = await fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } });
      if (!cartResponse.ok) throw new Error('Cart unavailable');
      const cart = await cartResponse.json();
      if (cart.attributes && cart.attributes._balour_partner_ref) {
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: { _balour_partner_ref: '' } })
        });
      }
    } catch (error) { /* Retry on a later consent or page event. */ }
    finally { syncing = false; }
  }

  function clearRevokedAttribution() {
    const shouldClearCart = hasStoredReferral() || syncing;
    clearStored();
    if (shouldClearCart) clearCartAttribution();
  }

  function handleConsentChange() {
    if (attributionAllowed()) {
      if (pendingIncoming) save(pendingIncoming);
      pendingIncoming = null;
      if (pendingPartnerCode) exchangePartnerCode();
      else if (current()) syncCart(true);
    } else {
      pendingIncoming = null;
      clearRevokedAttribution();
    }
  }

  const url = new URL(window.location.href);
  const incoming = url.searchParams.get('ba_ref');
  const partnerCode = url.searchParams.get('ba_partner');
  if (incoming !== null || partnerCode !== null) {
    if (validToken(incoming)) pendingIncoming = incoming;
    if (partnerCode && /^[A-Za-z0-9]{3,24}$/.test(partnerCode)) {
      pendingPartnerCode = partnerCode.toUpperCase();
      const cleanLanding = new URL(url.href);
      cleanLanding.searchParams.delete('ba_ref');
      cleanLanding.searchParams.delete('ba_partner');
      pendingLandingPath = `${cleanLanding.pathname}${cleanLanding.search}`;
      pendingUtm = Object.fromEntries([...cleanLanding.searchParams].filter(([key, value]) => /^utm_(source|medium|campaign|term|content)$/.test(key) && value.length <= 200));
    }
    removeReferralFromUrl(url);
  }

  if (attributionAllowed()) {
    if (pendingIncoming) save(pendingIncoming);
    pendingIncoming = null;
    if (pendingPartnerCode) exchangePartnerCode();
    else syncCart(Boolean(incoming));
  } else {
    clearRevokedAttribution();
  }
  try {
    if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) handleConsentChange();
      });
    }
  } catch (error) { /* Without the privacy API, referral storage remains disabled. */ }
  document.addEventListener('visitorConsentCollected', handleConsentChange);
  document.addEventListener('cart:updated', () => attributionAllowed() ? syncCart(false) : clearRevokedAttribution());
  document.addEventListener('cart:refresh', () => attributionAllowed() ? syncCart(false) : clearRevokedAttribution());
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form instanceof HTMLFormElement && /\/cart\/add(?:\.js)?(?:\?|$)/.test(form.action)) window.setTimeout(() => syncCart(false), 900);
  }, true);
  window.addEventListener('pageshow', () => {
    if (attributionAllowed()) {
      if (pendingPartnerCode) exchangePartnerCode();
      else syncCart(false);
    }
    else {
      clearRevokedAttribution();
    }
  });
})();
